import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  nextCourtNumber,
  useCreateCourtMutation,
  useUpdateCourtMutation,
  type ApiClubCourt,
} from '@/features/competition';
import { ApiError } from '@/shared/api/ApiError';
import { Alert, Button, Input, Label, Modal, useToast } from '@/shared/ui';

export interface CourtFormDialogProps {
  open: boolean;
  onClose: () => void;
  clubId: string;
  /** Wszystkie korty klubu — podpowiedź pierwszego wolnego numeru. */
  courts: readonly ApiClubCourt[];
  /** Kort do edycji; brak = tworzenie nowego. */
  court?: ApiClubCourt;
}

/**
 * Okno dodawania / edycji kortu klubu: numer, nazwa i nawierzchnia.
 *
 * Numer podpowiadamy jako pierwszy wolny, ale organizator może go zmienić —
 * duplikat w klubie odrzuci backend (409) i pokażemy jego komunikat.
 */
export function CourtFormDialog({ open, onClose, clubId, courts, court }: CourtFormDialogProps) {
  const { t } = useTranslation('tournament');
  const { showToast } = useToast();

  const isEdit = court !== undefined;
  const [number, setNumber] = useState(() => String(court?.number ?? nextCourtNumber(courts)));
  const [name, setName] = useState(() => court?.name ?? `Kort ${nextCourtNumber(courts)}`);
  const [surface, setSurface] = useState(() => court?.surface ?? '');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateCourtMutation(clubId);
  const updateMutation = useUpdateCourtMutation(clubId);
  const pending = createMutation.isPending || updateMutation.isPending;

  function describe(err: unknown): string {
    return err instanceof ApiError ? err.message : t('infrastructure.toast.courtFailed');
  }

  function handleSubmit() {
    const parsedNumber = Number(number);
    if (!Number.isInteger(parsedNumber) || parsedNumber < 1 || !name.trim()) {
      setError(t('infrastructure.toast.courtFailed'));
      return;
    }
    setError(null);

    if (isEdit && court) {
      updateMutation.mutate(
        {
          courtId: court.id,
          payload: { number: parsedNumber, name: name.trim(), surface: surface.trim() || null },
        },
        {
          onSuccess: () => {
            showToast({ tone: 'success', description: t('infrastructure.toast.courtUpdated') });
            onClose();
          },
          onError: (err) => setError(describe(err)),
        },
      );
      return;
    }

    createMutation.mutate(
      { number: parsedNumber, name: name.trim(), surface: surface.trim() || null },
      {
        onSuccess: () => {
          showToast({ tone: 'success', description: t('infrastructure.toast.courtCreated') });
          onClose();
        },
        onError: (err) => setError(describe(err)),
      },
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={
        isEdit
          ? t('infrastructure.court.editTitle', { name: court?.name ?? '' })
          : t('infrastructure.court.createTitle')
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('infrastructure.court.cancel')}
          </Button>
          <Button loading={pending} onClick={handleSubmit}>
            {isEdit ? t('infrastructure.court.save') : t('infrastructure.court.create')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && <Alert tone="danger">{error}</Alert>}

        <div className="grid gap-4 sm:grid-cols-[6rem_minmax(0,1fr)]">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="court-number">{t('infrastructure.court.number')}</Label>
            <Input
              id="court-number"
              type="number"
              min={1}
              value={number}
              onChange={(event) => setNumber(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="court-name">{t('infrastructure.court.name')}</Label>
            <Input
              id="court-name"
              required
              placeholder={t('infrastructure.court.namePlaceholder')}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="court-surface">{t('infrastructure.court.surface')}</Label>
          <Input
            id="court-surface"
            placeholder={t('infrastructure.court.surfacePlaceholder')}
            value={surface}
            onChange={(event) => setSurface(event.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
